"use client";

import { useCallback, useId, useMemo, useState } from "react";
import { Notice } from "@/components/tool-kit/primitives";
import type {
  SourceField,
  SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import {
  CitationForm,
  type FormOutput,
} from "@/components/tools/special-sources/citation-form";
import { LookupRow } from "@/components/tools/special-sources/lookup-row";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseCff } from "@/lib/tool-kit/special-sources/cff";
import {
  GITHUB_FIELDS as F,
  formatGithub,
  type GithubLabels,
  type GithubRelease,
  type GithubRepo,
  githubIsReady,
  type PreferredCitation,
  parseRepoInput,
  preferredBibtex,
  preferredCitation,
  valuesFromCff,
  valuesFromRepo,
} from "@/lib/tool-kit/special-sources/github-citation-generator";
import { formatQuestionnaire } from "@/lib/tool-kit/special-sources/questionnaire-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const GITHUB_CITATION_GENERATOR_STRINGS = {
  formHeading: "Software details",
  resultsHeading: "Your citations",
  emptyPrompt:
    "Look up a repository, or enter the software's name, to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  lookupLabel: "GitHub repository",
  lookupPlaceholder:
    "https://github.com/citation-file-format/citation-file-format",
  lookupButton: "Look up",
  lookupLoading: "Looking up…",
  lookupHint:
    "The tool reads the repository's public details from GitHub's API, and its CITATION.cff file if it has one. Only the repository address is sent, to GitHub.",
  lookupInvalid:
    "That is not a GitHub repository address. Paste one like https://github.com/owner/repository.",
  lookupNotFound:
    "GitHub has no public repository at {repo}. Check the address, or fill in the form by hand.",
  lookupRateLimited:
    "GitHub allows 60 requests an hour from one connection without signing in, and this connection has used them. Try again after {time}, or fill in the form by hand.",
  lookupFailed:
    "GitHub could not be reached just now. Try again in a moment, or fill in the form by hand.",
  lookupDoneCff:
    "Filled in from the CITATION.cff file in {repo} — the citation its authors provide. Check the details before you copy.",
  lookupDoneRepo:
    "{repo} has no CITATION.cff, so the form was filled from GitHub's repository details. GitHub only knows the account name: replace it with the real authors if the project lists them.",
  preferredNotice:
    "This repository's CITATION.cff asks you to cite another work ({type}) as well as, or instead of, the software: “{title}”. Its citations follow the software's.",
  authorsLabel: "Authors, one per line",
  authorsPlaceholder: "Stephan Druskat\nJurriaan H. Spaaks",
  authorsHint:
    "People as First Last or Last, First. A group such as The pandas development team is kept whole; wrap any other group name in braces: {Mozilla}.",
  titleLabel: "Software name",
  titlePlaceholder: "Citation File Format",
  titleHint: "Exactly as the developers write it, capitals and all.",
  versionLabel: "Version",
  versionPlaceholder: "1.2.0",
  dateLabel: "Release date",
  datePlaceholder: "YYYY-MM-DD",
  dateHint:
    "The date of the version you used. Leave it blank if there is none.",
  publisherLabel: "Published by",
  publisherPlaceholder: "GitHub",
  publisherHint:
    "GitHub for a repository; Zenodo or another archive when the release has a DOI there.",
  doiLabel: "DOI (optional)",
  doiPlaceholder: "10.5281/zenodo.5171937",
  urlLabel: "Repository URL",
  urlPlaceholder: "https://github.com/owner/repository",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes and bibliography)",
  bibtexLabel: "BibTeX and biblatex",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  worksCited: "Works Cited",
  inText: "In-text citation",
  footnote: "Footnote",
  bibliography: "Bibliography",
  bibtexMisc: "BibTeX (@misc)",
  biblatexSoftware: "biblatex (@software)",
  apaNote:
    "APA cites software with its version and the description [Computer software]. A DOI, when there is one, replaces the GitHub URL.",
  mlaNote:
    "MLA keeps the software's name exactly as written. Code on GitHub takes GitHub as a second container; an archived release takes its archive and DOI.",
  chicagoNote:
    "Chicago lists software under its developer in the bibliography. Give the version you used.",
  bibtexNote:
    "Classic BibTeX has no @software type, so @misc carries the version in a note. biblatex has @software.",
  preferredApaLabel: "Preferred citation — APA 7th edition",
  preferredMlaLabel: "Preferred citation — MLA 9th edition",
  preferredChicagoLabel: "Preferred citation — Chicago 18th edition",
  preferredBibtexLabel: "Preferred citation — BibTeX",
  preferredBibtexEntry: "BibTeX",
  preferredNote:
    "Taken from the CITATION.cff as written — check the title's capitalization against the published article.",
  example: {
    authors:
      "Druskat, Stephan\nSpaaks, Jurriaan H.\nChue Hong, Neil\nHaines, Robert\nBaker, James\nBliven, Spencer\nWillighagen, Egon\nPérez-Suárez, David\nKonovalov, Olexandr",
    title: "Citation File Format",
    version: "1.2.0",
    date: "2021-08-09",
    publisher: "Zenodo",
    doi: "10.5281/zenodo.5171937",
    url: "https://github.com/citation-file-format/citation-file-format",
  },
} as const;

type Strings = typeof GITHUB_CITATION_GENERATOR_STRINGS;

function buildFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.authors,
      kind: "people",
      label: strings.authorsLabel,
      placeholder: strings.authorsPlaceholder,
      hint: strings.authorsHint,
      wide: true,
    },
    {
      id: F.title,
      kind: "text",
      label: strings.titleLabel,
      placeholder: strings.titlePlaceholder,
      hint: strings.titleHint,
    },
    {
      id: F.version,
      kind: "text",
      label: strings.versionLabel,
      placeholder: strings.versionPlaceholder,
    },
    {
      id: F.date,
      kind: "date",
      label: strings.dateLabel,
      placeholder: strings.datePlaceholder,
      hint: strings.dateHint,
    },
    {
      id: F.publisher,
      kind: "text",
      label: strings.publisherLabel,
      placeholder: strings.publisherPlaceholder,
      hint: strings.publisherHint,
    },
    {
      id: F.doi,
      kind: "text",
      label: strings.doiLabel,
      placeholder: strings.doiPlaceholder,
    },
    {
      id: F.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholder,
    },
  ];
}

/** The widget's strings, as the formatter's labels. */
export function githubLabels(strings: Strings): GithubLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    bibtex: strings.bibtexLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    worksCited: strings.worksCited,
    inText: strings.inText,
    footnote: strings.footnote,
    bibliography: strings.bibliography,
    bibtexMisc: strings.bibtexMisc,
    biblatexSoftware: strings.biblatexSoftware,
    apaNote: strings.apaNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
    bibtexNote: strings.bibtexNote,
  };
}

/** The software's citations, with the BibTeX entries shown as code. */
export function githubOutputs(
  values: SourceValues,
  strings: Strings
): FormOutput[] {
  if (!githubIsReady(values)) {
    return [];
  }
  return formatGithub(values, githubLabels(strings)).map((output) =>
    output.key === "bibtex"
      ? {
          ...output,
          entries: output.entries.map((entry) => ({ ...entry, code: true })),
        }
      : output
  );
}

/** A CFF preferred citation, formatted as the journal article it usually is. */
export function preferredOutputs(
  pref: PreferredCitation,
  strings: Strings
): FormOutput[] {
  const bibtex: FormOutput = {
    key: "preferred-bibtex",
    label: strings.preferredBibtexLabel,
    entries: [
      {
        label: strings.preferredBibtexEntry,
        parts: [{ text: preferredBibtex(pref) }],
        code: true,
      },
    ],
  };
  if (pref.type !== "article") {
    return [bibtex];
  }
  const labels = {
    apa: strings.preferredApaLabel,
    mla: strings.preferredMlaLabel,
    chicago: strings.preferredChicagoLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    namingTest: "",
    worksCited: strings.worksCited,
    inText: strings.inText,
    footnote: strings.footnote,
    bibliography: strings.bibliography,
    apaNote: strings.preferredNote,
    mlaNote: strings.preferredNote,
    chicagoNote: strings.preferredNote,
  };
  return [
    ...formatQuestionnaire(pref.values, "article", labels).map((output) => ({
      ...output,
      key: `preferred-${output.key}`,
    })),
    bibtex,
  ];
}

type LookupState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "done"; message: string }
  | { status: "error"; message: string };

interface LookupResult {
  values: Record<string, string>;
  preferred: PreferredCitation | null;
  fromCff: boolean;
}

/** Repository details, its CITATION.cff, and — without one — its latest release. */
async function lookUpRepository(
  owner: string,
  repo: string
): Promise<Response | LookupResult> {
  const api = `https://api.github.com/repos/${owner}/${repo}`;
  const response = await fetch(api);
  if (!response.ok) {
    return response;
  }
  const record = (await response.json()) as GithubRepo;
  const branch = record.default_branch ?? "HEAD";
  const cff = await fetch(
    `https://raw.githubusercontent.com/${owner}/${repo}/${branch}/CITATION.cff`
  );
  if (cff.ok) {
    const doc = parseCff(await cff.text());
    return {
      values: valuesFromCff(doc, record.html_url ?? ""),
      preferred: preferredCitation(doc),
      fromCff: true,
    };
  }
  const latest = await fetch(`${api}/releases/latest`);
  const release = latest.ok ? ((await latest.json()) as GithubRelease) : null;
  return {
    values: valuesFromRepo(record, release),
    preferred: null,
    fromCff: false,
  };
}

export default function GithubCitationGeneratorWidget() {
  const strings = useToolUi(GITHUB_CITATION_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const lookupId = useId();
  const [values, setValues] = useState<SourceValues>({});
  const [address, setAddress] = useState("");
  const [lookup, setLookup] = useState<LookupState>({ status: "idle" });
  const [preferred, setPreferred] = useState<PreferredCitation | null>(null);
  const fields = useMemo(() => buildFields(strings), [strings]);
  const outputs = useMemo(() => {
    const software = githubOutputs(values, strings);
    return preferred && software.length > 0
      ? [...software, ...preferredOutputs(preferred, strings)]
      : software;
  }, [values, preferred, strings]);

  const update = useCallback((id: string, value: string) => {
    setValues((current) => ({ ...current, [id]: value }));
  }, []);

  function failure(response: Response, repo: string): string {
    const remaining = response.headers.get("x-ratelimit-remaining");
    if (
      (response.status === 403 || response.status === 429) &&
      remaining === "0"
    ) {
      const reset = Number(response.headers.get("x-ratelimit-reset"));
      const time = Number.isFinite(reset)
        ? new Intl.DateTimeFormat(locale, {
            hour: "numeric",
            minute: "2-digit",
          }).format(new Date(reset * 1000))
        : "";
      return t(strings.lookupRateLimited, { time });
    }
    if (response.status === 404) {
      return t(strings.lookupNotFound, { repo });
    }
    return strings.lookupFailed;
  }

  async function runLookup() {
    const ref = parseRepoInput(address);
    if (!ref) {
      setLookup({ status: "error", message: strings.lookupInvalid });
      return;
    }
    const repo = `${ref.owner}/${ref.repo}`;
    setLookup({ status: "loading" });
    try {
      const result = await lookUpRepository(ref.owner, ref.repo);
      if (result instanceof Response) {
        setLookup({ status: "error", message: failure(result, repo) });
        return;
      }
      setValues(result.values);
      setPreferred(result.preferred);
      setLookup({
        status: "done",
        message: t(
          result.fromCff ? strings.lookupDoneCff : strings.lookupDoneRepo,
          {
            repo,
          }
        ),
      });
    } catch {
      setLookup({ status: "error", message: strings.lookupFailed });
    }
  }

  return (
    <CitationForm
      fields={fields}
      intro={
        <div className="space-y-3">
          <LookupRow
            buttonLabel={strings.lookupButton}
            error={lookup.status === "error" ? lookup.message : ""}
            hint={strings.lookupHint}
            id={`${lookupId}-repo`}
            label={strings.lookupLabel}
            loading={lookup.status === "loading"}
            loadingLabel={strings.lookupLoading}
            message={lookup.status === "done" ? lookup.message : ""}
            onChange={setAddress}
            onLookup={runLookup}
            placeholder={strings.lookupPlaceholder}
            value={address}
          />
          {preferred ? (
            <Notice>
              {t(strings.preferredNotice, {
                type: preferred.type,
                title: preferred.values.title,
              })}
            </Notice>
          ) : null}
        </div>
      }
      onChange={update}
      onClear={() => {
        setValues({});
        setPreferred(null);
        setLookup({ status: "idle" });
      }}
      onExample={() => {
        setValues({ ...strings.example });
        setPreferred(null);
      }}
      outputs={outputs}
      strings={strings}
      values={values}
    />
  );
}
